import { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import loadingSpinner from "../../../assets/loading.gif";

export const EditSalaryAdvanceModal = ({ isOpen, onClose, requestData, onUpdate }) => {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (requestData && isOpen) {
      setAmount(requestData.amount?.toString() || "");
      setReason(requestData.reason || "");
      setErrors({});
    }
  }, [requestData, isOpen]);

  if (!isOpen || !requestData) return null;

  const validateForm = () => {
    const newErrors = {};

    if (!amount || parseFloat(amount) <= 0) {
      newErrors.amount = "Please enter a valid amount greater than 0.";
    }

    if (parseFloat(amount) > 1000000) {
      newErrors.amount = "Amount cannot exceed ₱1,000,000.";
    }

    if (!reason || reason.trim() === "") {
      newErrors.reason = "Please provide a reason for the request.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        await onUpdate(requestData.id, parseFloat(amount), reason.trim());
        onClose();
      } finally {
        setLoading(false);
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
      minimumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto"
      onClick={onClose}
    >
      <div
        className="bg-gray-50 w-full md:w-[90%] lg:w-[50%] rounded-lg shadow-xl h-auto max-h-[95vh] flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800">
            Edit Salary Advance Request
          </h2>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none cursor-pointer"
            aria-label="Close modal"
          >
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-grow space-y-6">
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <img src={loadingSpinner} alt="loading" className="w-12 h-12" />
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Reference Number Display */}
              <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm pb-4">
                <div className="py-2 border-b border-gray-300 mb-4">
                  <h3 className="font-medium text-gray-700 uppercase text-sm tracking-wide">
                    Request Information
                  </h3>
                </div>

                <div className="mb-4">
                  <label className="mb-1 block text-sm font-medium text-gray-500">
                    Reference Number
                  </label>
                  <div className="text-gray-800 font-medium bg-gray-100 p-2 rounded">
                    {requestData.formattedReferenceNumber || requestData.referenceNumber || "N/A"}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="mb-1 block text-sm font-medium text-gray-500">
                    Amount Requested *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₱</span>
                    <input
                      type="number"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className={`w-full pl-8 pr-3 py-2 border ${errors.amount ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700`}
                      placeholder="0.00"
                    />
                  </div>
                  {errors.amount && (
                    <p className="mt-1 text-sm text-red-500">{errors.amount}</p>
                  )}
                  {requestData.amount && !amount && (
                    <p className="mt-1 text-xs text-gray-500">
                      Previous amount: {formatCurrency(requestData.amount)}
                    </p>
                  )}
                </div>

                <div className="mb-2">
                  <label className="mb-1 block text-sm font-medium text-gray-500">
                    Reason *
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={4}
                    className={`w-full px-3 py-2 border ${errors.reason ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700 resize-vertical`}
                    placeholder="Please provide a reason for this salary advance request..."
                  />
                  {errors.reason && (
                    <p className="mt-1 text-sm text-red-500">{errors.reason}</p>
                  )}
                </div>
              </div>

              {/* Warning Note */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      Note: You can only edit this request while it is still Pending and no approvers have voted.
                    </p>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white rounded-b-lg shadow-sm">
          <button
            onClick={onClose}
            className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2 rounded border-none cursor-pointer transition-colors"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded border-none cursor-pointer font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};